import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Pagination,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined, ShopOutlined, SwapOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import type { StoreInfo, UserInfo } from '../types';

const { Text } = Typography;

interface StoreSwitchModalProps {
  open: boolean;
  onClose: () => void;
}

export const isValidStoreSessionPayload = (data: any) => (
  typeof data?.token === 'string'
  && data.token.length > 0
  && typeof data.emp_id === 'string'
  && data.emp_id.length > 0
  && typeof data.store_id === 'string'
  && data.store_id.length > 0
  && typeof data.store_name === 'string'
  && data.store_name.length > 0
  && typeof data.role_code === 'string'
  && data.role_code.length > 0
  && Array.isArray(data.menus)
  && Array.isArray(data.routes)
  && Array.isArray(data.page_permissions)
  && Array.isArray(data.button_permissions)
);

const getErrorMessage = (error: unknown, fallback: string) => {
  const responseData = (error as { responseData?: { msg?: string } })?.responseData;
  return responseData?.msg || (error as Error)?.message || fallback;
};

const StoreSwitchModal: React.FC<StoreSwitchModalProps> = ({ open, onClose }) => {
  const { user, switchStoreSession } = useAuth();
  const [stores, setStores] = useState<StoreInfo[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [switchingStoreId, setSwitchingStoreId] = useState('');
  const [loadError, setLoadError] = useState('');

  const loadStores = useCallback(async (
    nextKeyword: string,
    nextPage: number,
    nextPageSize: number,
  ) => {
    setLoading(true);
    setLoadError('');
    try {
      const res: any = await authApi.availableStores({
        keyword: nextKeyword,
        page: nextPage,
        pagesize: nextPageSize,
      });
      const data = res?.data || {};
      setStores(Array.isArray(data.list) ? data.list : []);
      setTotal(Number(data.total) || 0);
      setPage(Number(data.page) || nextPage);
      setPageSize(Number(data.pagesize) || nextPageSize);
    } catch (error) {
      setStores([]);
      setTotal(0);
      setLoadError(getErrorMessage(error, '门店列表加载失败，请重试'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setKeywordInput('');
    setKeyword('');
    setPage(1);
    setPageSize(10);
    setSwitchingStoreId('');
    loadStores('', 1, 10);
  }, [loadStores, open]);

  const handleSearch = (value: string) => {
    const nextKeyword = value.trim();
    setKeywordInput(nextKeyword);
    setKeyword(nextKeyword);
    setPage(1);
    loadStores(nextKeyword, 1, pageSize);
  };

  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    const normalizedPage = nextPageSize !== pageSize ? 1 : nextPage;
    setPage(normalizedPage);
    setPageSize(nextPageSize);
    loadStores(keyword, normalizedPage, nextPageSize);
  };

  const performSwitch = async (store: StoreInfo) => {
    if (!user || store.store_id === user.store_id || switchingStoreId) return;
    setSwitchingStoreId(store.store_id);
    try {
      const res: any = await authApi.switchStore({
        target_store_id: store.store_id,
        expected_store_id: user.store_id,
      });
      const data = res?.data || {};
      if (!isValidStoreSessionPayload(data)) {
        const responseError = new Error('切换门店返回数据异常，请重试或联系管理员');
        message.error(responseError.message);
        throw responseError;
      }

      const nextUser: UserInfo = {
        emp_id: data.emp_id,
        emp_name: data.emp_name,
        mobile: data.mobile,
        store_id: data.store_id,
        store_name: data.store_name,
        role_code: data.role_code,
        menus: data.menus,
        routes: data.routes,
        page_permissions: data.page_permissions,
        button_permissions: data.button_permissions,
      };
      switchStoreSession(data.token, nextUser);
      message.success(`已切换至 ${data.store_name}`);
      window.location.assign('/');
    } finally {
      setSwitchingStoreId('');
    }
  };

  const confirmSwitch = (store: StoreInfo) => {
    if (!user || store.store_id === user.store_id || switchingStoreId) return;
    Modal.confirm({
      title: `切换至 ${store.store_name}`,
      content: '切换后将进入目标门店首页，当前页面未保存的内容会丢失。确认继续吗？',
      okText: '确认切换',
      cancelText: '取消',
      centered: true,
      onOk: () => performSwitch(store),
    });
  };

  return (
    <Modal
      title={(
        <Space>
          <SwapOutlined />
          <span>切换门店</span>
        </Space>
      )}
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      centered
      destroyOnHidden
      closable={!switchingStoreId}
      maskClosable={!switchingStoreId}
      keyboard={!switchingStoreId}
    >
      <Input.Search
        value={keywordInput}
        onChange={(event) => setKeywordInput(event.target.value)}
        onSearch={handleSearch}
        placeholder="搜索门店名称或门店编码"
        enterButton="搜索"
        allowClear
        loading={loading}
        disabled={!!switchingStoreId || loading}
        style={{ marginBottom: 16 }}
      />

      {loadError && (
        <Alert
          type="error"
          showIcon
          message={loadError}
          action={(
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => loadStores(keyword, page, pageSize)}
            >
              重试
            </Button>
          )}
          style={{ marginBottom: 12 }}
        />
      )}

      <List
        loading={loading}
        dataSource={stores}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可切换门店" /> }}
        renderItem={(store) => {
          const isCurrent = store.is_current || store.store_id === user?.store_id;
          const isPaused = Number(store.store_status) === 2;
          return (
            <List.Item
              actions={[
                isCurrent ? (
                  <Button key="current" disabled>当前门店</Button>
                ) : (
                  <Button
                    key="switch"
                    type="primary"
                    icon={<SwapOutlined />}
                    loading={switchingStoreId === store.store_id}
                    disabled={!!switchingStoreId}
                    onClick={() => confirmSwitch(store)}
                  >
                    切换
                  </Button>
                ),
              ]}
              style={{ paddingInline: 4 }}
            >
              <List.Item.Meta
                avatar={(
                  <Avatar
                    size={44}
                    src={store.store_logo || undefined}
                    icon={<ShopOutlined />}
                    style={{ backgroundColor: '#1677ff' }}
                  />
                )}
                title={(
                  <Space size={8} wrap>
                    <Text strong>{store.store_name}</Text>
                    {isCurrent && <Tag color="blue">当前门店</Tag>}
                    {isPaused && <Tag color="orange">暂停营业</Tag>}
                  </Space>
                )}
                description={(
                  <Space size={16} wrap>
                    <Text type="secondary">门店编码：{store.store_id}</Text>
                    <Text type="secondary">当前角色：{store.role_name || store.role_code}</Text>
                  </Space>
                )}
              />
            </List.Item>
          );
        }}
      />

      {total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={[10, 20, 50]}
            showTotal={(count) => `共 ${count} 家门店`}
            onChange={handlePageChange}
            disabled={!!switchingStoreId || loading}
          />
        </div>
      )}
    </Modal>
  );
};

export default StoreSwitchModal;
